import { AlertCircle, Loader2, Pencil } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { ClientsService } from "../services/clientsService";
import type { ClientByIdResponse } from "../types/clients-api.types";
import { getTypeBadgeVariant } from "../utils/clientsTableHelpers";
import { EditClientForm } from "./EditClientForm";
import { EditEntityHeader } from "./EditEntityHeader";

export interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  onSuccess?: () => void;
}

export function EditClientModal({ isOpen, onClose, clientId, onSuccess }: EditClientModalProps) {
  const { t } = useTranslations("clients");
  const [client, setClient] = useState<ClientByIdResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadClient = useCallback(async (id: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data } = await ClientsService.fetchById(id);
      setClient(data);
    } catch (err) {
      setLoadError(HttpApiService.getErrorMessage(err));
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && clientId) {
      loadClient(clientId);
    } else if (!isOpen) {
      setClient(null);
      setLoadError(null);
    }
  }, [isOpen, clientId, loadClient]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  const handleSuccess = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const typeBadgeLabel = client
    ? client.type === "business"
      ? t("edit_field_type_business")
      : t("edit_field_type_individual")
    : "";

  return (
    <ColocaDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-warning" aria-hidden />
          {t("edit_modal_title")}
        </span>
      }
      description={
        client ? (
          <EditEntityHeader
            description={t("edit_modal_description")}
            typeBadgeLabel={typeBadgeLabel}
            typeBadgeVariant={getTypeBadgeVariant(client.type)}
          />
        ) : (
          t("edit_modal_description")
        )
      }
    >
      {loading && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t("edit_loading")}</p>
        </div>
      )}

      {loadError && !loading && <ColocaAlert variant="destructive" icon={AlertCircle} text={loadError} />}

      {client && !loading && !loadError && (
        <EditClientForm client={client} onSuccess={handleSuccess} onCancel={onClose} />
      )}
    </ColocaDrawer>
  );
}
